
import { useEffect } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import SessionTimeoutManager from "@/components/SessionTimeoutManager";
import { CriticalErrorMonitor } from "@/components/CriticalErrorMonitor";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Index from "./pages/Index";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import CategoryDetail from "./pages/CategoryDetail";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Accessibility from "./pages/Accessibility";
import Checkout from '@/pages/Checkout';
import Cart from '@/pages/Cart';
import PaymentSuccess from '@/pages/PaymentSuccess';
import AdminDashboard from "./pages/AdminDashboard";
import AdminProductCreate from "./pages/AdminProductCreate";
import NotFound from "./pages/NotFound";
import ProductEdit from "./pages/ProductEdit";
import WizardSearch from "./pages/WizardSearch";
import PaymentCancel from "./pages/PaymentCancel";
import Profile from "./pages/Profile";
import Favorites from "./pages/Favorites";
import EmailVerification from "./pages/EmailVerification";
import ScrollToTop from "./components/ScrollToTop";
import CartSidebar from "./components/CartSidebar";
import { SecurityHeaders } from "./components/SecurityHeaders";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { MaintenanceGuard } from "./components/MaintenanceGuard";
import BackToTop from "./components/BackToTop";
import WhatsAppButton from "./components/WhatsAppButton";
import AdPixels from "./components/AdPixels";
import MarketingConsentBanner from "./components/MarketingConsentBanner";

const queryClient = new QueryClient();

const App = () => {
  // Add message listener for iframe communication fallback
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Validate origin to prevent open-redirect attacks
      const allowedOrigins = [
        'https://upay.co.il',
        'https://www.upay.co.il',
        'https://grow.link',
        'https://www.grow.link',
        'https://grow.business',
        'https://www.grow.business',
        'https://paymentpage.grow.link',
        'https://sandboxapi.grow.link',
        'https://invoice4u.co.il',
        'https://www.invoice4u.co.il'
      ];

      if (!allowedOrigins.includes(event.origin)) {
        console.warn('Rejected postMessage from unauthorized origin:', event.origin);
        return;
      }

      if (event.data?.type === 'PAYMENT_SUCCESS_REDIRECT' && event.data?.url) {
        // Validate URL is relative or from an allowed origin
        try {
          const url = new URL(event.data.url, window.location.origin);
          if (url.origin === window.location.origin || allowedOrigins.includes(url.origin)) {
            console.log('Received iframe redirect message:', event.data.url);
            window.location.href = event.data.url;
          } else {
            console.error('Rejected redirect to unauthorized URL:', event.data.url);
          }
        } catch (e) {
          console.error('Invalid redirect URL:', event.data.url);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary component="App">
        <AuthProvider>
          <CartProvider>
            <FavoritesProvider>
              <SecurityHeaders enableCSP={true} enableHSTS={true} enableXFrameOptions={true} />
              <CriticalErrorMonitor />
              <TooltipProvider>
                <BrowserRouter>
                  <ScrollToTop />
                  <AdPixels />
                  <MarketingConsentBanner />
                  <Routes>
                    {/* All routes are protected by MaintenanceGuard */}
                    <Route path="/" element={<MaintenanceGuard><Index /></MaintenanceGuard>} />
                    <Route path="/products" element={<MaintenanceGuard><Products /></MaintenanceGuard>} />
                    <Route path="/product/:slug" element={<MaintenanceGuard><ProductDetail /></MaintenanceGuard>} />
                    <Route path="/category/:categoryName" element={<MaintenanceGuard><CategoryDetail /></MaintenanceGuard>} />
                    <Route path="/smart-agent" element={<MaintenanceGuard><WizardSearch /></MaintenanceGuard>} />
                    <Route path="/about" element={<MaintenanceGuard><About /></MaintenanceGuard>} />
                    <Route path="/contact" element={<MaintenanceGuard><Contact /></MaintenanceGuard>} />
                    <Route path="/terms" element={<MaintenanceGuard><Terms /></MaintenanceGuard>} />
                    <Route path="/privacy" element={<MaintenanceGuard><Privacy /></MaintenanceGuard>} />
                    <Route path="/accessibility" element={<MaintenanceGuard><Accessibility /></MaintenanceGuard>} />
                    <Route path="/cart" element={<MaintenanceGuard><Cart /></MaintenanceGuard>} />
                    <Route path="/checkout" element={<MaintenanceGuard><Checkout /></MaintenanceGuard>} />
                    <Route path="/email-verification" element={<MaintenanceGuard><EmailVerification /></MaintenanceGuard>} />
                    <Route path="/payment/success" element={<MaintenanceGuard><PaymentSuccess /></MaintenanceGuard>} />
                    <Route path="/payment/cancel" element={<MaintenanceGuard><PaymentCancel /></MaintenanceGuard>} />
                    <Route path="/profile" element={<MaintenanceGuard><ProtectedRoute><Profile /></ProtectedRoute></MaintenanceGuard>} />
                    <Route path="/favorites" element={<MaintenanceGuard><Favorites /></MaintenanceGuard>} />
                    <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
                    <Route path="/admin/product/edit/:id" element={<ProtectedRoute requireAdmin><ProductEdit /></ProtectedRoute>} />
                    <Route path="/admin/product/create" element={<ProtectedRoute requireAdmin><AdminProductCreate /></ProtectedRoute>} />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<MaintenanceGuard><NotFound /></MaintenanceGuard>} />
                  </Routes>

                  {/* Auth-dependent components rendered after routes */}
                  <SessionTimeoutManager />
                  <CartSidebar />
                  <BackToTop />
                  <WhatsAppButton />

                  {/* Global UI components */}
                  <Sonner />
                  <SpeedInsights />
                  <Analytics />
                </BrowserRouter>
              </TooltipProvider>
            </FavoritesProvider>
          </CartProvider>
        </AuthProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  );
};

export default App;
